import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import '@fontsource/material-icons/index.css'
import './GeoCartographyMainPage.css'

/**
 * GeoCartography — mapping-product landing page modelled on
 * `references/webpage_design/GeoCartography.html` (Material Design 3, dark
 * baseline, indigo-blue primary, teal tertiary).
 *
 * Interactive bits mirror SMETechFocusMain:
 *  - Top app bar gains an elevation/background once the page is scrolled.
 *  - Nav links highlight whichever section is in view (IntersectionObserver).
 *  - Anchor clicks smooth-scroll instead of jumping.
 *
 * Route wiring (this page lives at /geocartography):
 *  - "Explore NSW Transit" / NSW Transit card  → /nsw-transit
 *  - "Design System Demo" card                 → /tailwind-demo
 */

type NavKey = 'products' | 'about' | 'contact'

const NSW_TRANSIT_PATH = '/nsw-transit'
const TAILWIND_DEMO_PATH = '/tailwind-demo'

const GeoCartographyMainPage = () => {
  const [activeNav, setActiveNav] = useState<NavKey | null>(null)
  const [appBarScrolled, setAppBarScrolled] = useState(false)

  // ----- Sticky app-bar shadow on scroll -----
  useEffect(() => {
    const onScroll = () => {
      setAppBarScrolled(window.scrollY > 8)
      if (window.scrollY < 200) setActiveNav(null)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ----- Scroll-spy nav highlighting -----
  useEffect(() => {
    const ids: NavKey[] = ['products', 'about', 'contact']
    const sections = ids
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)
    if (sections.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveNav(entry.target.id as NavKey)
          }
        }
      },
      { rootMargin: '-50% 0px -45% 0px', threshold: 0 }
    )
    sections.forEach(s => observer.observe(s))
    return () => observer.disconnect()
  }, [])

  // Anchor click handler: smooth-scroll instead of jump.
  const onAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const target = id === 'top' ? document.body : document.getElementById(id)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="geocartography-main">

      {/* ============ TOP APP BAR ============ */}
      <header className={`md-top-app-bar${appBarScrolled ? ' md-top-app-bar--scrolled' : ''}`}>
        <div className="appbar-inner">
          <Link className="brand" to="/" aria-label="SMETechFocus home">
            <span className="brand__mark"><span className="material-icons" aria-hidden="true">hub</span></span>
            <span className="brand__text">
              <b>SMETechFocus</b>
              <span>GeoCartography</span>
            </span>
          </Link>
          <nav className="nav-links" aria-label="Primary">
            <a
              href="#products"
              onClick={e => onAnchorClick(e, 'products')}
              className={`md-nav-link${activeNav === 'products' ? ' md-nav-link--active' : ''}`}
            >Products</a>
            <a
              href="#about"
              onClick={e => onAnchorClick(e, 'about')}
              className={`md-nav-link${activeNav === 'about' ? ' md-nav-link--active' : ''}`}
            >About</a>
            <a
              href="#contact"
              onClick={e => onAnchorClick(e, 'contact')}
              className={`md-nav-link${activeNav === 'contact' ? ' md-nav-link--active' : ''}`}
            >Contact</a>
          </nav>
          <div className="appbar-actions">
            <a className="md-button md-button--filled has-icon" href="#contact" onClick={e => onAnchorClick(e, 'contact')}>
              <span className="material-icons" aria-hidden="true">north_east</span>
              Get in touch
            </a>
          </div>
        </div>
      </header>

      <main id="top">

        {/* ============ HERO ============ */}
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <span className="hero-eyebrow md-label-large">
                <span className="material-icons" aria-hidden="true">layers</span>
                Spatial data · Transit · Design
              </span>
              <h1 className="md-display-large">
                Professional <span className="accent">cartography</span>, built for modern apps.
              </h1>
              <p className="lead md-body-large">
                Interactive transit maps, real-time spatial data and cartographic
                design, engineered with precision and crafted for clarity.
              </p>
              <div className="hero-cta">
                <Link className="md-button md-button--filled has-icon" to={NSW_TRANSIT_PATH}>
                  <span className="material-icons" aria-hidden="true">directions_bus</span>
                  Explore NSW Transit
                </Link>
                <a className="md-button md-button--outlined has-icon" href="#products" onClick={e => onAnchorClick(e, 'products')}>
                  <span className="material-icons" aria-hidden="true">grid_view</span>
                  View all products
                </a>
              </div>
              <div className="hero-stats">
                <div className="hero-stat"><b>Live</b><span className="md-body-medium">Real-time positions</span></div>
                <div className="hero-stat"><b>Open</b><span className="md-body-medium">Built on open data</span></div>
                <div className="hero-stat"><b>Yours</b><span className="md-body-medium">Styled to your brand</span></div>
              </div>
            </div>

            {/* Cartographic panel */}
            <div className="map-panel" role="img" aria-label="Stylised transit map with route lines and stations">
              <div className="map-chip md-label-medium"><span className="dot" aria-hidden="true" />Live network</div>
              <svg viewBox="0 0 480 408" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <defs>
                  <pattern id="geo-grid" width="34" height="34" patternUnits="userSpaceOnUse">
                    <path d="M34 0H0V34" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.5" />
                  </pattern>
                </defs>
                <rect width="480" height="408" fill="url(#geo-grid)" />
                {/* contour curves */}
                <g fill="none" stroke="var(--md-sys-color-tertiary)" opacity="0.18">
                  <path d="M-20 120 C 90 60, 200 200, 330 110 S 520 90, 520 90" strokeWidth="2" />
                  <path d="M-20 160 C 90 100, 210 250, 340 150 S 520 130, 520 130" strokeWidth="2" />
                  <path d="M-20 300 C 120 250, 230 360, 360 300 S 520 290, 520 290" strokeWidth="2" />
                </g>
                {/* route: primary line */}
                <polyline
                  points="60,330 60,210 150,120 280,120 360,200 420,200"
                  fill="none" stroke="var(--md-sys-color-primary)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
                />
                {/* route: tertiary line */}
                <polyline
                  points="110,360 110,250 210,250 300,160 300,60"
                  fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"
                />
                {/* stations */}
                <g>
                  <circle cx="60" cy="210" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                  <circle cx="150" cy="120" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                  <circle cx="280" cy="120" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                  <circle cx="300" cy="160" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="4" />
                  <circle cx="210" cy="250" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="4" />
                </g>
              </svg>
              {/* pin */}
              <span className="map-pin material-icons" aria-hidden="true">location_on</span>
              <div className="map-controls">
                <button type="button" className="md-icon-button" aria-label="Zoom in"><span className="material-icons" aria-hidden="true">add</span></button>
                <button type="button" className="md-icon-button" aria-label="Zoom out"><span className="material-icons" aria-hidden="true">remove</span></button>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PRODUCTS ============ */}
        <section className="block block--alt" id="products">
          <div className="container">
            <div className="sec-head sec-head--center">
              <p className="sec-eyebrow md-label-large">Products</p>
              <h2 className="md-headline-large">Mapping tools, ready to deploy</h2>
              <p className="md-body-large">
                From live transit networks to global basemaps, across
                production-grade products and active explorations.
              </p>
            </div>
            <div className="product-grid">

              <Link className="md-card md-card--elevated product-card" to={NSW_TRANSIT_PATH}>
                <div className="pc-icon pc-icon--p"><span className="material-icons" aria-hidden="true">directions_bus</span></div>
                <h3 className="md-title-large">NSW Transit Map</h3>
                <p className="md-body-medium">
                  Interactive transit mapping for New South Wales with real-time
                  GTFS data integration.
                </p>
                <span className="pc-link">Explore now <span className="material-icons" aria-hidden="true">arrow_forward</span></span>
              </Link>

              <Link className="md-card md-card--elevated product-card" to={TAILWIND_DEMO_PATH}>
                <div className="pc-icon pc-icon--t"><span className="material-icons" aria-hidden="true">palette</span></div>
                <h3 className="md-title-large">Design System Demo</h3>
                <p className="md-body-medium">
                  A live showcase of the tokens and components powering our maps.
                </p>
                <div className="pc-badges">
                  <span className="md-badge md-badge--accent"><span className="material-icons" aria-hidden="true">auto_awesome</span>AI workflow</span>
                </div>
                <span className="pc-link">View demo <span className="material-icons" aria-hidden="true">arrow_forward</span></span>
              </Link>

              <div className="md-card md-card--filled product-card is-soon">
                <div className="pc-icon pc-icon--s"><span className="material-icons" aria-hidden="true">apartment</span></div>
                <h3 className="md-title-large">Urban Planning</h3>
                <p className="md-body-medium">
                  Advanced planning and development mapping tools for cities and
                  districts.
                </p>
                <span className="md-badge"><span className="material-icons" aria-hidden="true">schedule</span>Coming soon</span>
              </div>

              <div className="md-card md-card--filled product-card is-soon">
                <div className="pc-icon pc-icon--s"><span className="material-icons" aria-hidden="true">public</span></div>
                <h3 className="md-title-large">Global Mapping</h3>
                <p className="md-body-medium">
                  Comprehensive worldwide basemaps and tiling for international
                  projects.
                </p>
                <span className="md-badge"><span className="material-icons" aria-hidden="true">schedule</span>Coming soon</span>
              </div>

            </div>
          </div>
        </section>

        {/* ============ ABOUT ============ */}
        <section className="block" id="about">
          <div className="container about-grid">
            <div className="about-copy">
              <p className="sec-eyebrow md-label-large">About</p>
              <h2 className="md-headline-large" style={{ margin: '0 0 24px' }}>
                Cartographic expertise, modern engineering
              </h2>
              <p className="md-body-large">
                We build professional cartography solutions that pair cutting-edge
                technology with traditional mapping craft, making maps that are
                accurate, fast and genuinely usable.
              </p>
              <p className="md-body-large">
                From transit mapping to urban planning, our cartographers and
                engineers work side by side to deliver mapping experiences that
                hold up in production.
              </p>
              <div className="feature-row">
                <span className="md-chip"><span className="material-icons" aria-hidden="true">verified</span>Survey-grade accuracy</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">bolt</span>Real-time data</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">design_services</span>Custom design</span>
              </div>
            </div>
            <div className="stat-stack">
              <div className="md-card stat-card">
                <span className="material-icons" aria-hidden="true">layers</span>
                <div><b>Multi-source data</b><span className="md-body-medium">GTFS, GeoJSON &amp; live feeds</span></div>
              </div>
              <div className="md-card stat-card">
                <span className="material-icons" aria-hidden="true">bolt</span>
                <div><b>Real-time rendering</b><span className="md-body-medium">Live positions on MapLibre</span></div>
              </div>
              <div className="md-card stat-card">
                <span className="material-icons" aria-hidden="true">design_services</span>
                <div><b>Custom cartography</b><span className="md-body-medium">Styled to your brand tokens</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ CONTACT CTA ============ */}
        <section className="block block--alt" id="contact">
          <div className="container">
            <div className="cta-band">
              <h2 className="md-headline-large">Have a mapping project in mind?</h2>
              <p className="md-body-large">
                Tell us what you're building. We'll help you turn spatial data
                into clear, dependable maps.
              </p>
              <a className="md-button md-button--filled has-icon" href="mailto:andrew@smetechfocus.com.au">
                <span className="material-icons" aria-hidden="true">mail</span>
                andrew@smetechfocus.com.au
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Link className="brand" to="/" aria-label="SMETechFocus home">
                <span className="brand__mark"><span className="material-icons" aria-hidden="true">hub</span></span>
                <span className="brand__text">
                  <b>SMETechFocus</b>
                  <span>GeoCartography</span>
                </span>
              </Link>
              <p className="md-body-medium">Professional cartography solutions for the modern world.</p>
            </div>
            <div className="footer-col">
              <h4 className="md-title-medium">Products</h4>
              <ul className="md-body-medium">
                <li><Link to={NSW_TRANSIT_PATH}>NSW Transit</Link></li>
                <li><Link to={TAILWIND_DEMO_PATH}>Design System Demo</Link></li>
                <li>Urban Planning</li>
                <li>Global Mapping</li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="md-title-medium">Contact</h4>
              <ul className="md-body-medium">
                <li className="footer-contact"><span className="material-icons" aria-hidden="true">mail</span>andrew@smetechfocus.com.au</li>
                <li className="footer-contact"><span className="material-icons" aria-hidden="true">location_on</span>Sydney, NSW</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="md-body-small">
              © 2026 SMETechFocus Pty Ltd (ABN: 58 169 611 607). Connected systems for machineries,logistics &amp; transit.
            </span>
            <div className="footer-social">
              <a className="md-icon-button" href="mailto:andrew@smetechfocus.com.au" aria-label="Email">
                <span className="material-icons" aria-hidden="true">mail</span>
              </a>
              <Link to={NSW_TRANSIT_PATH} className="md-icon-button" aria-label="GeoCartography">
                <span className="material-icons" aria-hidden="true">map</span>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default GeoCartographyMainPage
